import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import {
  ShieldWarningIcon,
  CheckCircleIcon,
  ProhibitIcon,
  ArrowClockwiseIcon,
  UserIcon,
  PackageIcon,
  VideoCameraIcon,
} from "@phosphor-icons/react";

const TARGET_BADGE = {
  user: { Icon: UserIcon, label: "User", color: "bg-zinc-100 text-zinc-700 border-zinc-200" },
  listing: { Icon: PackageIcon, label: "Listing", color: "bg-sky-50 text-sky-700 border-sky-200" },
  stream: { Icon: VideoCameraIcon, label: "Stream", color: "bg-rose-50 text-rose-700 border-rose-200" },
};

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function AdminUserReports() {
  const [status, setStatus] = useState("open");
  const [targetType, setTargetType] = useState("all");
  const [search, setSearch] = useState("");
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ open: 0, resolved: 0, dismissed: 0, total: 0 });
  const [loading, setLoading] = useState(false);
  const [decideId, setDecideId] = useState(null);
  const [decideNote, setDecideNote] = useState("");
  const [decideStatus, setDecideStatus] = useState("resolved");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("status", status);
      if (targetType !== "all") params.set("target_type", targetType);
      if (search.trim()) params.set("q", search.trim());
      const res = await api.get(`/admin/user-reports?${params.toString()}`);
      setItems(res.data.items || []);
      setCounts(res.data.counts || { open: 0, resolved: 0, dismissed: 0, total: 0 });
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [status, targetType, search]);

  useEffect(() => {
    load();
  }, [load]);

  const decide = async (id, statusValue, note) => {
    try {
      await api.patch(`/admin/user-reports/${id}`, {
        status: statusValue,
        admin_note: note || null,
      });
      toast.success(statusValue === "resolved" ? "Marked resolved" : statusValue === "dismissed" ? "Dismissed" : "Reopened");
      setDecideId(null);
      setDecideNote("");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const StatusTab = ({ value, label, n, accent }) => (
    <button
      type="button"
      onClick={() => setStatus(value)}
      className={`px-3 py-1.5 rounded-full text-xs font-bold border transition ${
        status === value
          ? `${accent} border-transparent`
          : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-400"
      }`}
      data-testid={`reports-tab-${value}`}
    >
      {label} <span className="ml-1 opacity-75">({n})</span>
    </button>
  );

  return (
    <div className="mt-10 border border-zinc-200 bg-white rounded-2xl overflow-hidden" id="reports">
      <div className="p-4 border-b border-zinc-200 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <ShieldWarningIcon size={20} className="text-amber-600" />
          <div>
            <div className="font-display font-bold">User reports</div>
            <div className="text-xs text-zinc-500 mt-0.5 font-mono">
              {counts.total} total · every report from any user lands here
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Input
            placeholder="Search reason / handle / email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            className="h-9 w-56 text-sm"
            data-testid="reports-search-input"
          />
          <select
            value={targetType}
            onChange={(e) => setTargetType(e.target.value)}
            className="h-9 px-2 text-sm rounded-md border border-zinc-200 bg-white"
            data-testid="reports-target-filter"
          >
            <option value="all">All types</option>
            <option value="user">Users</option>
            <option value="listing">Listings</option>
            <option value="stream">Streams</option>
          </select>
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            disabled={loading}
            className="h-9"
            data-testid="reports-refresh-btn"
          >
            <ArrowClockwiseIcon size={14} className={loading ? "animate-spin" : ""} />
          </Button>
        </div>
      </div>

      <div className="px-4 pt-3 pb-2 flex items-center gap-2 flex-wrap border-b border-zinc-100">
        <StatusTab value="open" label="Open" n={counts.open} accent="bg-amber-500 text-white" />
        <StatusTab value="resolved" label="Resolved" n={counts.resolved} accent="bg-emerald-600 text-white" />
        <StatusTab value="dismissed" label="Dismissed" n={counts.dismissed} accent="bg-zinc-700 text-white" />
        <StatusTab value="all" label="All" n={counts.total} accent="bg-[#1D78AB] text-white" />
      </div>

      {loading ? (
        <div className="p-8 text-center text-sm text-zinc-500">Loading…</div>
      ) : items.length === 0 ? (
        <div className="p-10 text-center">
          <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 mb-2">
            <CheckCircleIcon size={24} className="text-emerald-600" />
          </div>
          <p className="text-sm text-zinc-600">
            {status === "open"
              ? "No open reports — your community is healthy ✨"
              : "No reports match this filter."}
          </p>
        </div>
      ) : (
        <ul className="divide-y divide-zinc-100">
          {items.map((r) => {
            const tBadge = TARGET_BADGE[r.target_type] || TARGET_BADGE.user;
            const TIcon = tBadge.Icon;
            const targetLink =
              r.target_type === "listing"
                ? `/listing/${r.target_id}`
                : r.target_type === "stream"
                ? `/stream/${r.target_id}`
                : r.reported_user?.username
                ? `/u/${r.reported_user.username}`
                : null;
            return (
              <li
                key={r.id}
                className="p-4 hover:bg-zinc-50 flex flex-col gap-3"
                data-testid={`report-row-${r.id}`}
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex items-start gap-3 min-w-0">
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold border ${tBadge.color}`}
                    >
                      <TIcon size={12} />
                      {tBadge.label}
                    </span>
                    <div className="min-w-0">
                      <div className="text-sm">
                        <span className="font-mono text-zinc-500">
                          @{r.reporter?.username || r.reporter?.name || "anon"}
                        </span>{" "}
                        reported{" "}
                        {targetLink ? (
                          <Link
                            to={targetLink}
                            className="font-bold text-[#1D78AB] hover:underline"
                          >
                            {r.target_label ||
                              `@${r.reported_user?.username || r.reported_user?.name || "user"}`}
                          </Link>
                        ) : (
                          <span className="font-bold">
                            @{r.reported_user?.username || r.reported_user?.name || "user"}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-zinc-500 font-mono mt-0.5">
                        {fmtTime(r.created_at)}
                        {r.category ? ` · ${r.category}` : ""}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {r.status === "open" && (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold border bg-amber-50 text-amber-700 border-amber-200">
                        OPEN
                      </span>
                    )}
                    {r.status === "resolved" && (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold border bg-emerald-50 text-emerald-700 border-emerald-200">
                        RESOLVED
                      </span>
                    )}
                    {r.status === "dismissed" && (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold border bg-zinc-100 text-zinc-600 border-zinc-200">
                        DISMISSED
                      </span>
                    )}
                  </div>
                </div>

                <blockquote className="text-sm text-zinc-700 border-l-2 border-amber-400 pl-3 whitespace-pre-wrap">
                  {r.reason}
                </blockquote>

                {r.context && (
                  <div className="text-xs text-zinc-500 italic">
                    Context: {r.context}
                  </div>
                )}

                {r.admin_note && (
                  <div className="text-xs bg-zinc-50 border border-zinc-200 rounded p-2 text-zinc-600">
                    <span className="font-bold text-zinc-700">Admin note · </span>
                    {r.admin_note}
                    {r.decided_at && (
                      <span className="text-zinc-400 ml-2 font-mono">
                        ({fmtTime(r.decided_at)})
                      </span>
                    )}
                  </div>
                )}

                <div className="flex items-center gap-2 flex-wrap">
                  {decideId === r.id ? (
                    <div className="flex flex-col gap-2 w-full bg-zinc-50 border border-zinc-200 rounded-lg p-3">
                      <Textarea
                        value={decideNote}
                        onChange={(e) => setDecideNote(e.target.value)}
                        placeholder="Optional note (action taken, ban applied, no violation, etc.)"
                        rows={2}
                        className="text-sm"
                        data-testid={`report-note-${r.id}`}
                      />
                      <div className="flex items-center gap-2 flex-wrap">
                        <Button
                          size="sm"
                          onClick={() => {
                            setDecideStatus("resolved");
                            decide(r.id, "resolved", decideNote);
                          }}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white"
                          data-testid={`report-resolve-${r.id}`}
                        >
                          <CheckCircleIcon size={14} className="mr-1" /> Mark resolved
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setDecideStatus("dismissed");
                            decide(r.id, "dismissed", decideNote);
                          }}
                          data-testid={`report-dismiss-${r.id}`}
                        >
                          <ProhibitIcon size={14} className="mr-1" /> Dismiss
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setDecideId(null);
                            setDecideNote("");
                          }}
                        >
                          Cancel
                        </Button>
                        {r.reported_user && (
                          <Link
                            to={`/admin#users`}
                            className="text-xs text-zinc-500 underline ml-auto"
                          >
                            Open offender in Users tab →
                          </Link>
                        )}
                      </div>
                    </div>
                  ) : r.status === "open" ? (
                    <Button
                      size="sm"
                      onClick={() => {
                        setDecideId(r.id);
                        setDecideNote("");
                      }}
                      className="bg-[#1D78AB] hover:bg-[#176089] text-white"
                      data-testid={`report-decide-${r.id}`}
                    >
                      Decide…
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => decide(r.id, "open", null)}
                      data-testid={`report-reopen-${r.id}`}
                    >
                      Re-open
                    </Button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
